import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Flex,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  useToast,
  FormControl,
  FormLabel,
  Select,
  Spinner,
  Center,
} from "@chakra-ui/react";
import { FaLink, FaTrash } from "react-icons/fa";

const API_VINCULOS = "/api/frota/vinculo-motorista-veiculo";
const API_VEICULOS = "/api/frota/veiculos";
const API_MOTORISTAS = "/api/frota/motoristas";

export default function VinculoMotoristaVeiculoTab({ isActive = false }) {
  const [vinculos, setVinculos] = useState([]);
  const [veiculos, setVeiculos] = useState([]);
  const [motoristas, setMotoristas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [veiculoId, setVeiculoId] = useState("");
  const [motoristaId, setMotoristaId] = useState("");
  const toast = useToast();

  const loadVinculos = async () => {
    try {
      const res = await fetch(API_VINCULOS);
      if (!res.ok) throw new Error("Erro ao carregar vínculos");
      const data = await res.json();
      setVinculos(Array.isArray(data) ? data : []);
    } catch (err) {
      toast({
        title: "Erro",
        description: err.message,
        status: "error",
        isClosable: true,
      });
      setVinculos([]);
    }
  };

  const loadVeiculos = async () => {
    try {
      const res = await fetch(API_VEICULOS);
      if (res.ok) {
        const data = await res.json();
        setVeiculos(Array.isArray(data) ? data : []);
      }
    } catch {
      setVeiculos([]);
    }
  };

  const loadMotoristas = async () => {
    try {
      const res = await fetch(API_MOTORISTAS);
      if (res.ok) {
        const data = await res.json();
        setMotoristas(Array.isArray(data) ? data : []);
      }
    } catch {
      setMotoristas([]);
    }
  };

  useEffect(() => {
    if (!isActive) return;
    setLoading(true);
    Promise.all([loadVinculos(), loadVeiculos(), loadMotoristas()]).finally(
      () => setLoading(false)
    );
  }, [isActive]);

  const handleVincular = async () => {
    if (!veiculoId || !motoristaId) {
      toast({
        title: "Selecione o veículo e o motorista",
        status: "warning",
        isClosable: true,
      });
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(API_VINCULOS, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          motorista_id: motoristaId,
          veiculo_id: veiculoId,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Erro ao vincular");
      toast({
        title: "Vínculo criado com sucesso",
        status: "success",
        isClosable: true,
      });
      setVeiculoId("");
      setMotoristaId("");
      await loadVinculos();
    } catch (err) {
      toast({
        title: "Erro",
        description: err.message,
        status: "error",
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDesvincular = async (id) => {
    if (!window.confirm("Desvincular este motorista do veículo?")) return;
    try {
      const res = await fetch(`${API_VINCULOS}?id=${id}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Erro ao desvincular");
      toast({ title: "Vínculo removido", status: "success", isClosable: true });
      await loadVinculos();
    } catch (err) {
      toast({
        title: "Erro",
        description: err.message,
        status: "error",
        isClosable: true,
      });
    }
  };

  const nomeVeiculo = (row) => {
    if (row.veiculos && typeof row.veiculos === "object" && row.veiculos.placa)
      return row.veiculos.placa;
    return row.veiculo_id || "–";
  };

  const nomeMotorista = (row) => {
    if (
      row.motoristas &&
      typeof row.motoristas === "object" &&
      row.motoristas.nome
    )
      return row.motoristas.nome;
    return row.motorista_id || "–";
  };

  const veiculosVinculadosIds = new Set(
    vinculos.map((v) => v.veiculo_id).filter(Boolean)
  );
  const motoristasVinculadosIds = new Set(
    vinculos.map((v) => v.motorista_id).filter(Boolean)
  );
  const veiculosDisponiveis = veiculos.filter(
    (v) => !veiculosVinculadosIds.has(v.id)
  );
  const motoristasDisponiveis = motoristas.filter(
    (m) => !motoristasVinculadosIds.has(m.id)
  );

  return (
    <Box>
      <Heading size="md" color="#007abe" mb={6}>
        Vincular motorista ao veículo
      </Heading>

      <Box
        bg="white"
        p={4}
        borderRadius="xl"
        boxShadow="md"
        mb={6}
        borderWidth="1px"
        borderColor="gray.200"
      >
        <Flex gap={4} flexWrap="wrap" align="flex-end">
          <FormControl maxW="280px">
            <FormLabel>Veículo</FormLabel>
            <Select
              placeholder="Selecione o veículo"
              value={veiculoId}
              onChange={(e) => setVeiculoId(e.target.value)}
            >
              {veiculosDisponiveis.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.placa} {v.tipo ? `(${v.tipo})` : ""}
                </option>
              ))}
            </Select>
          </FormControl>
          <FormControl maxW="280px">
            <FormLabel>Motorista</FormLabel>
            <Select
              placeholder="Selecione o motorista"
              value={motoristaId}
              onChange={(e) => setMotoristaId(e.target.value)}
            >
              {motoristasDisponiveis.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.nome}
                </option>
              ))}
            </Select>
          </FormControl>
          <Button
            leftIcon={<FaLink />}
            bg="#007abe"
            color="white"
            onClick={handleVincular}
            isLoading={saving}
            _hover={{ bg: "#0066a3" }}
          >
            Vincular
          </Button>
        </Flex>
        <Box as="p" fontSize="sm" color="gray.500" mt={2}>
          Selecione um veículo e um motorista e clique em Vincular. O motorista
          ficará habilitado a esse veículo.
        </Box>
      </Box>

      <Heading size="sm" color="gray.700" mb={3}>
        Vínculos atuais
      </Heading>
      <TableContainer
        bg="white"
        borderRadius="xl"
        boxShadow="md"
        overflowX="auto"
        borderWidth="1px"
        borderColor="gray.200"
      >
        {loading ? (
          <Center py={12}>
            <Spinner size="xl" color="#007abe" />
          </Center>
        ) : (
          <Table variant="simple" size="sm">
            <Thead bg="#007abe">
              <Tr>
                <Th color="white">Veículo</Th>
                <Th color="white">Motorista</Th>
                <Th color="white" width="100px">
                  Ações
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {vinculos.map((row) => (
                <Tr key={row.id}>
                  <Td fontWeight="medium">{nomeVeiculo(row)}</Td>
                  <Td>{nomeMotorista(row)}</Td>
                  <Td>
                    <Button
                      size="xs"
                      colorScheme="red"
                      leftIcon={<FaTrash />}
                      onClick={() => handleDesvincular(row.id)}
                    >
                      Desvincular
                    </Button>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        )}
        {!loading && vinculos.length === 0 && (
          <Center py={8} color="gray.500">
            Nenhum vínculo cadastrado. Selecione veículo e motorista acima e
            clique em Vincular.
          </Center>
        )}
      </TableContainer>
    </Box>
  );
}
